/**
 * Authentication API client
 * Connects frontend to IAM service backend
 */

import axios, { AxiosInstance } from 'axios';
import { getAuthToken, setAuthToken, clearAuthToken, getRefreshToken, setRefreshToken } from '../utils/auth';
import { API_BASE_URL } from '@/config/apiBase';

// Create axios instance with interceptors
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token; omit Content-Type for FormData so multipart boundary is set
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data;
          setAuthToken(accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        clearAuthToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  full_name?: string;
  role?: 'innovator' | 'reviewer';
  institution_uuid?: string;
}

export interface AuthResponse {
  message: string;
  user: {
    user_uuid: string;
    email: string;
    full_name?: string;
    role: string;
    institution_uuid?: string;
    institution_name?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface UserProfile {
  user: {
    user_uuid: string;
    email: string;
    full_name?: string;
    role: string;
    institution_uuid?: string;
    institution_name?: string;
    subscription_tier?: string;
    created_at: string;
    last_login?: string;
    reviewer_profile?: {
      expertise_tags: string[];
      max_assignments: number;
      current_assignments: number;
      bio?: string;
    };
    /** Set when Paystack confirms innovator assessment fee (server-side). */
    innovator_assessment_fee_paid_at?: string | null;
  };
}

/**
 * Login user
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/login`, credentials);
    
    // Store tokens
    setAuthToken(response.data.tokens.accessToken);
    setRefreshToken(response.data.tokens.refreshToken);
    
    return response.data;
  } catch (error: any) {
    // Extract detailed error message
    if (error.response?.status === 401) {
      throw new Error('Invalid email or password');
    }
    throw new Error(error.response?.data?.message || error.message || 'Login failed');
  }
}

/**
 * Register new user
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
  try {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/register`, data);
    
    // Store tokens
    setAuthToken(response.data.tokens.accessToken);
    setRefreshToken(response.data.tokens.refreshToken);
    
    return response.data;
  } catch (error: any) {
    // Extract detailed error message
    if (error.response?.data?.details) {
      const validationErrors = error.response.data.details
        .map((err: any) => `${err.param}: ${err.msg}`)
        .join(', ');
      throw new Error(`Validation failed: ${validationErrors}`);
    }
    throw new Error(error.response?.data?.message || error.message || 'Registration failed');
  }
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    // Continue with logout even if API call fails
    console.error('Logout API call failed:', error);
  } finally {
    clearAuthToken();
  }
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfile>('/users/me');
  return response.data;
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post<{ accessToken: string }>(`${API_BASE_URL}/auth/refresh`, {
    refreshToken,
  });

  setAuthToken(response.data.accessToken);
  return response.data.accessToken;
}

/**
 * Request password reset email (forgot password).
 * Works for all profiles; backend sends a secure link valid for 1 hour.
 */
export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const response = await axios.post<{ message: string }>(`${API_BASE_URL}/auth/forgot-password`, {
    email: email.trim().toLowerCase(),
  });
  return response.data;
}

/**
 * Reset password with token from email link.
 * Token is one-time use and expires in 1 hour.
 */
export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const response = await axios.post<{ message: string }>(`${API_BASE_URL}/auth/reset-password`, {
    token,
    password: newPassword,
  });
  return response.data;
}

export default apiClient;
